import { afterNextRender, ChangeDetectionStrategy, Component, inject, input, DOCUMENT } from '@angular/core';

import { KeycloakAuthService } from '@dss-libs/shared/data-access/auth';
import { ActivatedRoute } from '@angular/router';
import { SigningMethodEnum, TSigningProviderName, VerificationMethod } from '@dss-libs/shared/type/common-types';
import { LOGIN_REDIRECT_URL_FN } from '@dss-libs/shared/utility/global-injection-tokens';

@Component({
  selector: 'dss-libs-feature-login',
  templateUrl: './login.component.html',
  styleUrl: './login.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoginComponent {
  private readonly keyCloakAuthService = inject(KeycloakAuthService);
  private readonly document = inject(DOCUMENT);
  private readonly route = inject(ActivatedRoute);
  private readonly redirectUrlFn = inject(LOGIN_REDIRECT_URL_FN);

  orderId = input('');
  recipientId = input('');
  signingMethod = input('');
  receiverEmail = input('');

  constructor() {
    afterNextRender(() => this.authenticateOrRegister(SigningMethodEnum.EMAIL));
  }

  /*
   * Redirects the user to the login page. If the user is invited from an email and the email does not exist in the system,
   * the user is redirected to the registration page if he chooses Email as the provider.
   * @param methodID - The signing provider name.
   */
  authenticateOrRegister(methodID: TSigningProviderName) {
    const redirectUrl = this.constructRedirectUrl(methodID);
    if (methodID === SigningMethodEnum.EMAIL && this.isUserInvited() && !this.isEmailExists()) {
      this.redirectToRegister(methodID, redirectUrl);
      return;
    }
    this.redirectToLogin(methodID, redirectUrl);
  }

  private constructRedirectUrl(methodID: TSigningProviderName) {
    const returnUrl = this.route.snapshot.queryParams['redirectUri'];

    return returnUrl
      ? `${this.document.location.origin}${returnUrl}`
      : `${this.document.location.origin}${this.redirectUrlFn(methodID, {
          orderId: this.orderId(),
          recipientId: this.recipientId(),
          ...(this.signingMethod() && { signingMethod: this.signingMethod() }),
          ...(this.receiverEmail() && { receiverEmail: this.receiverEmail() }),
          verificationMethod: VerificationMethod.Email,
        })}`;
  }

  private redirectToRegister(methodID: TSigningProviderName, redirectUrl: string) {
    this.keyCloakAuthService.registerWithProviderAndRedirectBack(methodID, redirectUrl);
  }

  private redirectToLogin(methodID: TSigningProviderName, redirectUrl: string) {
    this.keyCloakAuthService.signWithProviderAndRedirectBack(methodID, redirectUrl);
  }

  /*
   * Checks if the invited email exists in the system.
   */
  private isEmailExists() {
    const isEmailExists: string = this.route.snapshot.queryParams['IsEmailExists'];
    return isEmailExists?.toLowerCase() === 'true';
  }

  /*
   * Checks if a user is coming from an email invitation.
   */
  private isUserInvited() {
    return !!this.route.snapshot.queryParams['IsEmailExists'];
  }
}
